// Staff Operations Service Layer
// Shift logs, performance tracking, handover notes, daily checklists
//
// Hybrid storage: localStorage for instant reads, Firestore for
// cross-device sync. Writes are fire-and-forget to Firestore with
// Sentry capture on failure. Call `refreshStaffOps(departmentId)` on
// page mount to pull the latest from Firestore into localStorage.

import * as Sentry from '@sentry/react'
import { restSet, restDelete, restQuery } from './firestoreRest'

function lsKey(ns) { return `desres_pos_staff_${ns}` }

// Fire-and-forget Firestore write. Matches the store.js pattern so
// shift-handover data syncs across devices without blocking the UI.
function fsSet(collection, record) {
  if (!record || !record.id) return
  restSet(`${collection}/${record.id}`, record).catch(err => {
    console.warn(`[staffOps] ${collection} write failed:`, err?.message)
    Sentry.captureException(err, { extra: { module: 'staffOps', collection, recordId: record.id } })
  })
}

function load(ns) {
  try { return JSON.parse(localStorage.getItem(lsKey(ns)) || '[]') }
  catch { return [] }
}

function save(ns, data) {
  localStorage.setItem(lsKey(ns), JSON.stringify(data))
}

function genId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8) }

// ────────────────────────────────────────────
// Shift Logs
// ────────────────────────────────────────────

export function getShiftLogs(departmentId, date) {
  let all = load('shiftLogs')
  if (departmentId) all = all.filter(s => s.departmentId === departmentId)
  if (date) all = all.filter(s => s.date === date)
  return all.sort((a, b) => b.createdAt?.localeCompare(a.createdAt) || 0)
}

export function saveShiftLog(log) {
  const all = load('shiftLogs')
  const existing = all.findIndex(s => s.id === log.id)
  const record = {
    ...log,
    id: log.id || genId(),
    updatedAt: new Date().toISOString(),
    createdAt: log.createdAt || new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('shiftLogs', all)
  fsSet('shiftLogs', record)
  return record
}

export function deleteShiftLog(id) {
  save('shiftLogs', load('shiftLogs').filter(s => s.id !== id))
  restDelete(`shiftLogs/${id}`).catch(err => {
    console.warn('[staffOps] deleteShiftLog Firestore failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'staffOps', collection: 'shiftLogs', id } })
  })
}

// ────────────────────────────────────────────
// Staff Performance
// ────────────────────────────────────────────

export function getStaffPerformance(departmentId, dateFrom, dateTo) {
  const shifts = getShiftLogs(departmentId)
    .filter(s => (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo))

  const staffStats = {}
  for (const shift of shifts) {
    const name = shift.staffName || 'Unknown'
    if (!staffStats[name]) {
      staffStats[name] = { staffName: name, shifts: 0, totalHours: 0, salesProcessed: 0, unitsProduced: 0, notes: [] }
    }
    staffStats[name].shifts++
    staffStats[name].totalHours += shift.hoursWorked || 0
    staffStats[name].salesProcessed += shift.salesProcessed || 0
    staffStats[name].unitsProduced += shift.unitsProduced || 0
    if (shift.notes) staffStats[name].notes.push({ date: shift.date, note: shift.notes })
  }

  return Object.values(staffStats).map(s => ({
    ...s,
    avgHoursPerShift: s.shifts > 0 ? Number((s.totalHours / s.shifts).toFixed(1)) : 0,
    salesPerHour: s.totalHours > 0 ? Number((s.salesProcessed / s.totalHours).toFixed(1)) : 0,
    unitsPerHour: s.totalHours > 0 ? Number((s.unitsProduced / s.totalHours).toFixed(1)) : 0,
  })).sort((a, b) => b.shifts - a.shifts)
}

// ────────────────────────────────────────────
// Handover Notes (daily notes per department)
// ────────────────────────────────────────────

export function getHandoverNotes(departmentId, date) {
  let all = load('handoverNotes')
  if (departmentId) all = all.filter(n => n.departmentId === departmentId)
  if (date) all = all.filter(n => n.date === date)
  return all.sort((a, b) => b.createdAt?.localeCompare(a.createdAt) || 0)
}

export function saveHandoverNote(note) {
  const all = load('handoverNotes')
  const existing = all.findIndex(n => n.id === note.id)
  const record = {
    ...note,
    id: note.id || genId(),
    updatedAt: new Date().toISOString(),
    createdAt: note.createdAt || new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('handoverNotes', all)
  fsSet('handoverNotes', record)
  return record
}

export function deleteHandoverNote(id) {
  save('handoverNotes', load('handoverNotes').filter(n => n.id !== id))
  restDelete(`handoverNotes/${id}`).catch(err => {
    console.warn('[staffOps] deleteHandoverNote Firestore failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'staffOps', collection: 'handoverNotes', id } })
  })
}

// ────────────────────────────────────────────
// Daily Checklist
// ────────────────────────────────────────────

export const DEFAULT_CHECKLIST = [
  { key: 'morning_stock', label: 'Morning stock logged', required: true },
  { key: 'equipment_check', label: 'Equipment checked & working', required: true },
  { key: 'hygiene_check', label: 'Kitchen/area cleaned & sanitized', required: true },
  { key: 'staff_briefed', label: 'Staff briefed on daily plan', required: false },
  { key: 'stock_levels', label: 'Stock levels checked', required: true },
  { key: 'surplus_logged', label: 'End-of-day surplus logged', required: true },
  { key: 'cash_reconciled', label: 'Cash reconciled', required: true },
  { key: 'handover_written', label: 'Handover notes written', required: false },
]

export function getDailyChecklist(departmentId, date) {
  const key = `desres_pos_checklist_${departmentId}_${date}`
  try {
    const raw = localStorage.getItem(key)
    if (raw) return JSON.parse(raw)
  } catch {}
  return DEFAULT_CHECKLIST.map(c => ({ ...c, completed: false, completedBy: null, completedAt: null }))
}

export function saveDailyChecklist(departmentId, date, checklist) {
  const key = `desres_pos_checklist_${departmentId}_${date}`
  localStorage.setItem(key, JSON.stringify(checklist))
  // One doc per dept per date — deterministic ID
  const docId = `${departmentId}_${date}`.toLowerCase()
  const record = { id: docId, departmentId, date, items: checklist, updatedAt: new Date().toISOString() }
  fsSet('dailyChecklists', record)
}

// ────────────────────────────────────────────
// Firestore refresh — pulls latest data from Firestore into
// localStorage. Call from page mount (StaffOps.jsx, Accounting.jsx)
// so shift handovers from Device A become visible on Device B.
// Falls back silently to whatever's in localStorage on failure.
// ────────────────────────────────────────────

export async function refreshStaffOps(departmentId) {
  if (!departmentId) return
  try {
    const [shiftLogs, handoverNotes] = await Promise.all([
      restQuery('shiftLogs', 'departmentId', departmentId).catch(() => null),
      restQuery('handoverNotes', 'departmentId', departmentId).catch(() => null),
    ])
    if (Array.isArray(shiftLogs)) save('shiftLogs', shiftLogs)
    if (Array.isArray(handoverNotes)) save('handoverNotes', handoverNotes)
  } catch (err) {
    console.warn('[staffOps] refreshStaffOps failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'staffOps', fn: 'refreshStaffOps', departmentId } })
  }
}

export async function refreshDailyChecklist(departmentId, date) {
  if (!departmentId || !date) return null
  const key = `desres_pos_checklist_${departmentId}_${date}`
  try {
    const docId = `${departmentId}_${date}`.toLowerCase()
    const { restGet } = await import('./firestoreRest')
    const doc = await restGet(`dailyChecklists/${docId}`)
    if (doc && Array.isArray(doc.items)) {
      localStorage.setItem(key, JSON.stringify(doc.items))
      return doc.items
    }
  } catch (err) {
    console.warn('[staffOps] refreshDailyChecklist failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'staffOps', fn: 'refreshDailyChecklist', departmentId, date } })
  }
  return null
}

// ────────────────────────────────────────────
// Production Efficiency
// ────────────────────────────────────────────

export function getProductionEfficiency(departmentId, dateFrom, dateTo) {
  const shifts = getShiftLogs(departmentId)
    .filter(s => (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo))

  let totalPlanned = 0, totalActual = 0
  const daily = {}

  for (const shift of shifts) {
    const planned = shift.plannedUnits || 0
    const actual = shift.unitsProduced || 0
    totalPlanned += planned
    totalActual += actual

    if (!daily[shift.date]) daily[shift.date] = { planned: 0, actual: 0 }
    daily[shift.date].planned += planned
    daily[shift.date].actual += actual
  }

  const efficiency = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0

  return {
    period: { from: dateFrom, to: dateTo },
    totalPlanned,
    totalActual,
    efficiency: Number(efficiency.toFixed(1)),
    variance: totalActual - totalPlanned,
    daily: Object.entries(daily).map(([date, d]) => ({
      date,
      planned: d.planned,
      actual: d.actual,
      efficiency: d.planned > 0 ? Number(((d.actual / d.planned) * 100).toFixed(1)) : 0,
    })).sort((a, b) => a.date.localeCompare(b.date)),
    status: efficiency >= 95 ? 'excellent' : efficiency >= 80 ? 'good' : efficiency >= 60 ? 'needs_improvement' : 'critical',
  }
}
